/**
 * TasksRoleLandingGuard — Rollen-Landing beim Betreten des Aufgaben-Bereichs
 * (06-ENTRYPOINT-NAV.md §4).
 *
 * Ersetzt den statischen `'' → overview`-Redirect: leitet je Rolle auf den
 * passenden Screen (Admin/Manager/TeamLeader → S1 `overview`, Employee → S3
 * `my`, Terminal → S7 `shift-start`). Serverseitig wird der Scope zusätzlich
 * erzwungen (ein Employee auf einem Manager-Deep-Link wird server-scoped).
 *
 * ⚠️ INTEGRATIONS-HANDOFF (A1 ist Owner von `tasks-routing.module.ts`):
 * Damit die Rollen-Landing greift, muss die leere Redirect-Route dort auf
 * diesen Guard umgestellt werden — statt
 *   `{ path: '', redirectTo: 'overview', pathMatch: 'full' }`
 * z. B.:
 *   `{ path: '', canActivate: [TasksRoleLandingGuard], component: TasksOverviewPageComponent }`
 * (der Guard gibt IMMER ein `UrlTree` zurück und rendert die Komponente nie).
 */
import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { EMPLOYEE_SHELL_BASE } from 'src/app/modules/employee/desktop/shell/employee-nav.areas';
import { resolveTasksLanding, TasksRole } from './tasks-nav.logic';

@Injectable({ providedIn: 'root' })
export class TasksRoleLandingGuard implements CanActivate {
  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
  ) {}

  canActivate(): UrlTree {
    const role = this.currentRole();

    // Ein Mitarbeiter hat eine eigene Hülle mit Kopfleiste, Bereichsnavigation
    // und Abmelden. `/tasks/my` liegt AUSSERHALB davon: wer von dort kommt —
    // Direktlink, Benachrichtigung, Lesezeichen —, landete auf einer Seite
    // ohne jede Navigation und ohne Weg zurück. Dieselbe Fläche gibt es
    // innerhalb der Hülle, also dorthin.
    if (role === 'employee') {
      return this.router.createUrlTree([`${EMPLOYEE_SHELL_BASE}/aufgaben`]);
    }

    const landing = resolveTasksLanding(role);
    return this.router.createUrlTree(['/tasks', landing]);
  }

  /** Leitet die Rolle aus dem AuthService ab (Token-Claim). */
  private currentRole(): TasksRole {
    if (this.auth.isTerminal) return 'terminal';
    if (this.auth.roleMatch(['MANAGER'])) return 'manager';
    if (this.auth.roleMatch(['TEAMLEADER'])) return 'teamLeader';
    return 'employee';
  }
}
